import {
  PropsWithChildren,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Resource } from "i18next";
import {
  LocalizationContext as LocalizationContextType,
  LocalizationProviderProps,
  TranslateFunction,
} from "./types";
import { i18n } from "./i18n";
import { LocalizationContext } from "./localization-context";
import { languageChangeStub, setAppLanguage as saveLanguage } from "./utils";

export const LocalizationProvider = <
  Locale extends string = string,
  Translations extends Resource = Resource
>(
  props: PropsWithChildren<LocalizationProviderProps<Locale, Translations>>
) => {
  const {
    children,
    resources,
    language,
    fallBackLanguage,
    defaultNamespace,
    onLanguageChange = languageChangeStub,
  } = props;

  const [appLanguage, setAppLanguage] = useState<Locale>(
    language ?? fallBackLanguage
  );

  const translationInstance = useRef(
    i18n(resources, defaultNamespace, fallBackLanguage)
  );

  useEffect(() => {
    if (!language && translationInstance.current) {
      setAppLanguage(translationInstance.current.language as Locale);
    }
  }, []);

  useEffect(() => {
    if (language && translationInstance.current.language !== language) {
      translationInstance.current.changeLanguage(language);
      setAppLanguage(language);
    }
  }, [language, translationInstance]);

  const translate = useCallback(
    (...args: Parameters<TranslateFunction>) =>
      translationInstance.current.t(...args),
    [translationInstance]
  );

  const changeLanguage = useCallback(
    async (language: Locale) => {
      await Promise.all([
        translationInstance.current.changeLanguage(language),
        onLanguageChange(language),
      ]);
      saveLanguage(language);
      setAppLanguage(language);
    },
    [onLanguageChange, translationInstance]
  );

  /**
   * createContext не обладает достаточными возможностями для дженериков,
   * приходится кастить
   */
  const value = useMemo(
    () => ({
      language: appLanguage,
      translate,
      changeLanguage,
    }),
    [appLanguage, changeLanguage, translate]
  ) as unknown as LocalizationContextType;

  return (
    <LocalizationContext.Provider value={value}>
      {children}
    </LocalizationContext.Provider>
  );
};
